/**
 * RuleComplianceTable Component
 *
 * Displays rule compliance statistics in a sortable table
 */

import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowDownward as ArrowDownIcon,
  ArrowUpward as ArrowUpIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import {
  Box,
  IconButton,
  LinearProgress,
  Link,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type { RuleComplianceStatsResponse } from '@store/generated/jotaudit';

interface RuleComplianceTableProps {
  /** Array of rule compliance stats */
  rules: RuleComplianceStatsResponse[];
  /** Whether data is loading */
  isLoading?: boolean;
  /** Callback when clicking a rule row (for future drill-down) */
  onRuleClick?: (ruleId: string, ruleName?: string, complianceRate?: number, violationCount?: number) => void;
  /** Callback when clicking the edit button for a rule */
  onEditRule?: (ruleId: string) => void;
  /** Organization ID for admin mode */
  organizationId?: string;
}

type SortField = 'rule_name' | 'total_audits' | 'compliance_rate';
type SortDirection = 'asc' | 'desc';

/**
 * Format percentage
 */
const formatPercent = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return '—';
  return `${value.toFixed(1)}%`;
};

/**
 * Get color for compliance rate
 */
const getComplianceColor = (rate: number | null | undefined): string => {
  if (rate === null || rate === undefined) return '#6B7280'; // Gray
  if (rate >= 80) return '#22C55E'; // Green
  if (rate >= 50) return '#EAB308'; // Yellow
  return '#EF4444'; // Red
};

export const RuleComplianceStatsTable: React.FC<RuleComplianceTableProps> = ({
  rules,
  isLoading = false,
  onRuleClick,
  onEditRule,
  organizationId,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [sortField, setSortField] = useState<SortField>('total_audits');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  // Handle rule name click to navigate to analytics page
  const handleRuleNameClick = useCallback(
    (event: React.MouseEvent<HTMLElement>, ruleId: string, ruleName?: string, complianceRate?: number, violationCount?: number) => {
      event.stopPropagation();
      if (organizationId) {
        navigate(`/admin-v2/jotaudit/${organizationId}/rules/${ruleId}/analytics`);
      } else {
        navigate(`/jotaudit/rules/${ruleId}/analytics`);
      }
      onRuleClick?.(ruleId, ruleName, complianceRate, violationCount);
    },
    [navigate, onRuleClick, organizationId],
  );

  // Sort rules
  const sortedRules = useMemo(() => {
    const sorted = [...rules];
    sorted.sort((a, b) => {
      let aValue: number | string | null | undefined;
      let bValue: number | string | null | undefined;

      switch (sortField) {
        case 'rule_name':
          aValue = a.rule_name || a.rule_id;
          bValue = b.rule_name || b.rule_id;
          break;
        case 'total_audits':
          aValue = a.total_audits || 0;
          bValue = b.total_audits || 0;
          break;
        case 'compliance_rate':
          aValue = a.compliance_rate ?? null;
          bValue = b.compliance_rate ?? null;
          break;
      }

      // Handle null values
      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      // Compare values
      let comparison = 0;
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        comparison = aValue.localeCompare(bValue);
      } else {
        comparison = (aValue as number) - (bValue as number);
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });

    return sorted;
  }, [rules, sortField, sortDirection]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const SortIcon: React.FC<{ field: SortField }> = ({ field }) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? (
      <ArrowUpIcon sx={{ fontSize: 16, ml: 0.5 }} />
    ) : (
      <ArrowDownIcon sx={{ fontSize: 16, ml: 0.5 }} />
    );
  };

  if (isLoading && rules.length === 0) {
    return (
      <Paper
        elevation={0}
        sx={{
          border: `1px solid ${theme.palette.grey[200]}`,
          borderRadius: 2,
        }}
      >
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: theme.palette.grey[50] }}>
                <TableCell sx={{ fontWeight: 600 }}>Rule Name</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Total Audits</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Compliant</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Warning</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Non-Compliant</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Compliance Rate</TableCell>
                {onEditRule && <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>
                  <TableCell>
                    <Skeleton variant="text"  sx={{ width: 200, height: 20 }}/>
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="text"  sx={{ width: 80, height: 20 }}/>
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="text"  sx={{ width: 60, height: 20 }}/>
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="text"  sx={{ width: 60, height: 20 }}/>
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="text"  sx={{ width: 60, height: 20 }}/>
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="text"  sx={{ width: 100, height: 20 }}/>
                  </TableCell>
                  {onEditRule && (
                    <TableCell>
                      <Skeleton variant="circular"  sx={{ width: 24, height: 24 }}/>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    );
  }

  if (rules.length === 0) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 3,
          border: `1px solid ${theme.palette.grey[200]}`,
          borderRadius: 2,
          textAlign: 'center',
        }}
      >
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
          No rule compliance data available
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        border: `1px solid ${theme.palette.grey[200]}`,
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: theme.palette.grey[50] }}>
              <TableCell
                sx={{
                  fontWeight: 600,
                  cursor: 'pointer',
                  userSelect: 'none',
                  '&:hover': { bgcolor: theme.palette.grey[100] },
                }}
                onClick={() => handleSort('rule_name')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Rule Name
                  <SortIcon field="rule_name" />
                </Box>
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 600,
                  cursor: 'pointer',
                  userSelect: 'none',
                  '&:hover': { bgcolor: theme.palette.grey[100] },
                }}
                onClick={() => handleSort('total_audits')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Total Audits
                  <SortIcon field="total_audits" />
                </Box>
              </TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Compliant</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Warning</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Non-Compliant</TableCell>
              <TableCell
                sx={{
                  fontWeight: 600,
                  cursor: 'pointer',
                  userSelect: 'none',
                  '&:hover': { bgcolor: theme.palette.grey[100] },
                }}
                onClick={() => handleSort('compliance_rate')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Compliance Rate
                  <SortIcon field="compliance_rate" />
                </Box>
              </TableCell>
              {onEditRule && (
                <TableCell sx={{ fontWeight: 600, width: 80 }}>Actions</TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedRules.map(rule => (
              <TableRow
                key={rule.rule_id}
                sx={{
                  '&:hover': {
                    bgcolor: theme.palette.grey[50],
                  },
                }}
              >
                <TableCell>
                  <Link
                    component="button"
                    variant="body2"
                    onClick={(e: React.MouseEvent<HTMLElement>) => 
                      handleRuleNameClick(
                        e, 
                        rule.rule_id, 
                        rule.rule_name || rule.rule_id,
                        rule.compliance_rate ?? undefined,
                        rule.non_compliant_count || 0
                      )
                    }
                    sx={{
                      fontWeight: 500,
                      textAlign: 'left',
                      cursor: 'pointer',
                      textDecoration: rule.is_deleted ? 'line-through' : 'none',
                      color: rule.is_disabled
                        ? theme.palette.text.disabled
                        : rule.is_deleted
                          ? theme.palette.text.secondary
                          : theme.palette.primary.main,
                      opacity: rule.is_disabled ? 0.6 : 1,
                      '&:hover': {
                        textDecoration: rule.is_deleted ? 'line-through' : 'underline',
                      },
                    }}
                  >
                    {rule.rule_name || rule.rule_id}
                  </Link>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{rule.total_audits || 0}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ color: theme.palette.success.main }}>
                    {rule.compliant_count || 0}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ color: theme.palette.warning.main }}>
                    {rule.warning_count || 0}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ color: theme.palette.error.main }}>
                    {rule.non_compliant_count || 0}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ flex: 1, minWidth: 80 }}>
                      <LinearProgress
                        variant="determinate"
                        value={rule.compliance_rate || 0}
                        sx={{
                          height: 8,
                          borderRadius: 1,
                          bgcolor: theme.palette.grey[200],
                          '& .MuiLinearProgress-bar': {
                            bgcolor: getComplianceColor(rule.compliance_rate),
                          },
                        }}
                      />
                    </Box>
                    <Typography variant="body2" sx={{ minWidth: 50, textAlign: 'right' }}>
                      {formatPercent(rule.compliance_rate)}
                    </Typography>
                  </Box>
                </TableCell>
                {onEditRule && (
                  <TableCell>
                    <Tooltip title="Edit rule with AI">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          onEditRule(rule.rule_id);
                        }}
                        disabled={rule.is_deleted}
                        sx={{
                          color: theme.palette.primary.main,
                          '&:hover': {
                            bgcolor: theme.palette.primary.main + '10',
                          },
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default RuleComplianceStatsTable;

